import { useMemo, useState, useSyncExternalStore } from 'react'
import { toast } from 'sonner'
import { api, extractApiError } from './api'
import {
  DISPLAY_DEFAULTS,
  isValidDisplayValue,
  resolveDisplayPreferences,
} from './display-prefs-defs.js'
import {
  DEFAULT_PAGE_SIZES,
  countChanged,
  effectiveDisplay,
  legacyTextSizeMigration,
  pageSizeFor,
  pageSizeOptions,
  pendingEdits,
} from './display-prefs-core.js'
import { onServerPreferences } from './space-prefs'
import { setPersonalFormats } from './format'

/**
 * Account-level display & accessibility preferences — the ONE store every
 * surface reads and writes: the My Preferences page, the header quick
 * controls (theme switch, text size), the tables and `App.jsx`.
 *
 *   header control ─┐
 *   preferences page ┼─► this store ─► PUT /me/preferences ─► every page
 *   tables ─────────┘
 *
 * Source of truth: the SERVER (`UserPreference.display`, per user). It is
 * hydrated by the same `GET /me/preferences` the favourites sync already
 * makes (see `onServerPreferences` in space-prefs.js), on sign-in and on tab
 * focus.
 *
 * Browser storage is only a paint cache, never the source of truth:
 *   - `taskmagix.display.<userId>` — that user's last saved set, so a reload
 *     paints their choice before the server answers;
 *   - `taskmagix.display.boot`     — the set last applied on this browser,
 *     read before React renders to avoid a light→dark flash. Cleared on
 *     sign-out, so the next person on the computer never inherits it.
 *
 * `saved` — what the server holds (after a successful GET / PUT).
 * `edits` — ONLY the keys the My Preferences page has changed and not yet
 *           saved; laid over `saved` on screen live, never sent until Save.
 *           `null` when nothing is pending. (Exposed to React as `preview`.)
 */

const BOOT_KEY = 'taskmagix.display.boot'
const userKey = (userId) => `taskmagix.display.${userId}`
// The header's old browser-only text size (per user) — read once for the
// one-time migration into the account; never written any more.
const legacyFontKey = (userId) => `taskmagix.space.font-size.${userId}`

const hasWindow = typeof window !== 'undefined'
const media = (q) =>
  hasWindow && typeof window.matchMedia === 'function' ? window.matchMedia(q) : null
const MQ = {
  dark: '(prefers-color-scheme: dark)',
  motion: '(prefers-reduced-motion: reduce)',
  contrast: '(prefers-contrast: more)',
}

function readJson(key) {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}
function writeJson(key, value) {
  try {
    if (value == null) localStorage.removeItem(key)
    else localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* private mode / quota — the cache is only an optimisation */
  }
}

/** What the computer currently asks for — read live, never cached. */
export function readSystemDisplay() {
  return {
    prefersDark: Boolean(media(MQ.dark)?.matches),
    prefersReducedMotion: Boolean(media(MQ.motion)?.matches),
    prefersMoreContrast: Boolean(media(MQ.contrast)?.matches),
  }
}

let state = {
  userId: null,
  saved: resolveDisplayPreferences(null),
  // Only the keys the page has TOUCHED (null = nothing pending). Never a full
  // copy of `saved`, so a re-sync that updates `saved` (another tab, another
  // device, the header controls) shows through for every untouched key, and
  // Save can never send a value the person didn't choose.
  edits: null,
  hydrated: false,
  saving: false,
  system: readSystemDisplay(),
}
let snapshot = null
const listeners = new Set()

// Stale-response guards. A GET snapshot that started before the latest local
// write began, or before the last sign-out, is out of date and ignored; a PUT
// response is applied only if no newer write started after it, and never
// after a sign-out.
let lastWriteStartedAt = 0
let lastResetAt = 0
let writeSeq = 0
let resetEpoch = 0

const effectiveOf = (s) => (s.edits ? { ...s.saved, ...s.edits } : s.saved)

function getSnapshot() {
  if (!snapshot) {
    const effective = effectiveOf(state)
    snapshot = {
      ...state,
      preview: state.edits,
      effective,
      changedCount: countChanged(state.saved, state.edits ? effective : null),
    }
  }
  return snapshot
}

function setState(patch) {
  state = { ...state, ...patch }
  snapshot = null
  applyToDocument()
  listeners.forEach((l) => l())
}

/** Paint the effective set onto <html>. Every visual effect of these
 *  preferences hangs off these attributes in index.css — no component needs
 *  to know they exist. */
function applyToDocument() {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const eff = effectiveDisplay(effectiveOf(state), state.system)
  root.classList.toggle('dark', eff.theme === 'dark')
  root.style.colorScheme = eff.theme
  root.dataset.textSize = eff.textSize
  root.dataset.motion = eff.motion
  root.dataset.contrast = eff.contrast
  root.dataset.underline = eff.underline
  // Region & formats: every shared formatter (lib/format.js) writes dates,
  // times and numbers with these — including a live preview on My Preferences.
  setPersonalFormats(effectiveOf(state))
  // Paint cache = the SAVED set only, never an unsaved preview.
  if (state.userId) writeJson(BOOT_KEY, state.saved)
}

/**
 * Call once, before React renders: paint the last-applied set (no flash) and
 * follow live changes to the computer's light/dark, reduced-motion and
 * contrast settings while a preference is on "Computer".
 */
export function initDisplayPrefs() {
  const boot = readJson(BOOT_KEY)
  // Before this store existed the header theme lived in `taskmagix.theme`;
  // honour it for the very first paint only (never sent to the server — it
  // was shared by everyone on the browser, so it can't be attributed to one
  // account).
  let legacyTheme = null
  try {
    legacyTheme = localStorage.getItem('taskmagix.theme')
  } catch {
    /* ignore */
  }
  const first = boot ?? (isValidDisplayValue('theme', legacyTheme) ? { theme: legacyTheme } : null)
  setState({ saved: resolveDisplayPreferences(first) })
  for (const q of Object.values(MQ)) {
    const mq = media(q)
    if (!mq) continue
    const onChange = () => setState({ system: readSystemDisplay() })
    if (mq.addEventListener) mq.addEventListener('change', onChange)
    else if (mq.addListener) mq.addListener(onChange)
  }
}

/** Apply a server snapshot for `userId` (from GET /me/preferences, which
 *  started at `startedAt`). Untouched keys follow the server; the page's
 *  unsaved edits stay pending. Runs the one-time text-size migration. */
async function hydrateFromServer(userId, data, startedAt = Date.now()) {
  if (!userId || !data?.display) return
  if (startedAt < lastResetAt || startedAt < lastWriteStartedAt) return // stale
  if (state.userId !== userId) {
    // Different account on this browser: start from THAT user's cache.
    setState({ userId, edits: null, saved: resolveDisplayPreferences(readJson(userKey(userId))) })
  }
  let saved = resolveDisplayPreferences(data.display)
  const legacy = readJson(legacyFontKey(userId))
  const patch = legacyTextSizeMigration(data.displayUnset, legacy?.level)
  if (patch) {
    const epoch = resetEpoch
    lastWriteStartedAt = Date.now()
    try {
      const res = await api.put('/me/preferences', { display: patch })
      saved = resolveDisplayPreferences(res.data?.display)
    } catch {
      // Keep the browser value on screen this session; retried next sign-in.
      saved = { ...saved, ...patch }
    }
    if (epoch !== resetEpoch) return // signed out meanwhile
  }
  writeJson(userKey(userId), saved)
  setState({ userId, saved, edits: pendingEdits(saved, state.edits), hydrated: true })
}
onServerPreferences(hydrateFromServer)

/** Sign-out: forget the user and the boot cache so the next person on this
 *  computer starts from the defaults, never from the previous account. Any
 *  request still in flight for the previous user is ignored when it lands. */
export function resetDisplayPrefsForSignOut() {
  resetEpoch += 1
  lastResetAt = Date.now()
  setState({ userId: null, edits: null, saved: resolveDisplayPreferences(null), hydrated: false })
  writeJson(BOOT_KEY, null)
}

/* ── Preferences page: live preview, Save, Discard, Reset ──────────────── */

/** Preview one change on screen without saving it. */
export function previewDisplayPref(key, value) {
  if (!isValidDisplayValue(key, value)) return
  setState({ edits: { ...(state.edits ?? {}), [key]: value } })
}

/** Preview the canonical defaults (Reset). Nothing is saved until Save. */
export function previewDisplayDefaults() {
  setState({ edits: { ...DISPLAY_DEFAULTS } })
}

/** Throw the preview away and put the saved set back on screen. No request. */
export function discardDisplayPreview() {
  if (state.edits) setState({ edits: null })
}

/** Persist every pending edit in one PUT. Edits made while the request is in
 *  flight stay pending afterwards (they are not silently dropped). */
export async function saveDisplayPreview() {
  const patch = pendingEdits(state.saved, state.edits)
  if (!patch) {
    setState({ edits: null })
    return true
  }
  const seq = ++writeSeq
  const epoch = resetEpoch
  lastWriteStartedAt = Date.now()
  setState({ saving: true })
  try {
    const res = await api.put('/me/preferences', { display: patch })
    if (epoch !== resetEpoch) return true
    const saved = resolveDisplayPreferences(res.data?.display)
    if (state.userId) writeJson(userKey(state.userId), saved)
    setState({
      saved: seq === writeSeq ? saved : { ...state.saved, ...patch },
      edits: pendingEdits(saved, state.edits),
      saving: false,
    })
    return true
  } catch (err) {
    setState({ saving: false })
    throw err
  }
}

/* ── Header quick controls: change + save in one step ──────────────────── */

/**
 * Set one preference from a header control: applied at once and saved to the
 * account (the same server value the page edits). If the page has the same
 * key pending, the quick change replaces that pending edit. While
 * impersonating, the server is read-only: the change applies on screen for
 * this session only, without an error.
 */
export async function setDisplayPref(key, value) {
  if (!isValidDisplayValue(key, value)) return
  const previousValue = state.saved[key]
  const edits = state.edits ? { ...state.edits } : null
  if (edits) delete edits[key]
  setState({ saved: { ...state.saved, [key]: value }, edits: edits && Object.keys(edits).length ? edits : null })
  const seq = ++writeSeq
  const epoch = resetEpoch
  lastWriteStartedAt = Date.now()
  try {
    const res = await api.put('/me/preferences', { display: { [key]: value } })
    // Ignore a response overtaken by a newer write or by a sign-out.
    if (epoch !== resetEpoch || seq !== writeSeq) return
    const confirmed = resolveDisplayPreferences(res.data?.display)
    if (state.userId) writeJson(userKey(state.userId), confirmed)
    setState({ saved: confirmed, edits: pendingEdits(confirmed, state.edits) })
  } catch (err) {
    if (epoch !== resetEpoch) return
    if (err?.response?.data?.error?.code === 'impersonation_read_only') return
    // Roll back only THIS key, and only if nothing has changed it since.
    if (state.saved[key] === value) {
      setState({ saved: { ...state.saved, [key]: previousValue } })
    }
    toast.error(extractApiError(err).message || 'Your preference could not be saved.')
  }
}

/* ── React ─────────────────────────────────────────────────────────────── */

const subscribe = (cb) => {
  listeners.add(cb)
  return () => listeners.delete(cb)
}
const serverSnapshot = getSnapshot()

/** `{ saved, preview, effective, changedCount, hydrated, saving, system }` */
export function useDisplayPrefs() {
  return useSyncExternalStore(subscribe, getSnapshot, () => serverSnapshot)
}

/** The saved value of one preference (what other screens should use). */
export function useDisplayPref(key) {
  return useDisplayPrefs().saved[key]
}

/**
 * Page size for a paginated list, starting from the person's account
 * rows-per-page. It follows the preference live (e.g. once the server copy
 * loads) until the person picks a size on THIS list, which then wins for as
 * long as the list is open.
 *
 *   const [limit, setLimit, limitOptions] = usePageSize({ max: 100 })
 *   <Pagination limit={limit} onLimitChange={setLimit} limitOptions={limitOptions} />
 *
 * `options` — the sizes this list offers (default 25/50/100/200/500);
 * `max`     — the largest page its API accepts, so the menu never offers a
 *             size the server would reject;
 * `initial` — a size this list was opened with (e.g. a `?limit=` link),
 *             which wins over the preference like a pick would.
 */
export function usePageSize({ options = DEFAULT_PAGE_SIZES, max = Infinity, initial = null } = {}) {
  const preferred = useDisplayPref('rowsPerPage')
  const optionsKey = options.join(',')
  const limitOptions = useMemo(
    () => pageSizeOptions(options, max),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by value
    [optionsKey, max],
  )
  const [chosen, setChosen] = useState(initial)
  const limit = chosen != null && limitOptions.includes(chosen) ? chosen : pageSizeFor(preferred, limitOptions)
  return [limit, setChosen, limitOptions]
}

/** Non-React read of the saved set (e.g. a hook's useState initialiser). */
export function getSavedDisplayPrefs() {
  return state.saved
}

export const __displayPrefsInternals = { getSnapshot, setState }
